import { ReportQuestion } from '../../types/reports';

export interface PdfRepeatedAnswer {
  key: string;
  label: string;
  value: any;
}

export interface PdfRepeatedItem {
  key: string;
  itemLabel: string;
  answers: PdfRepeatedAnswer[];
}

export interface PdfRenderableQuestion {
  key: string;
  questionNumber: number;
  label: string;
  value: any;
  repeatedItems?: PdfRepeatedItem[];
}

interface BuildPdfRenderableQuestionsArgs {
  sectionQuestions: ReportQuestion[];
  formValues: Record<string, any>;
  getQuestionNumber: (questionId: number) => number;
  formatValue: (value: any, type: ReportQuestion['type']) => any;
  getOrdinalWord: (num: number) => string;
  maxRepeatCount: number;
}

export const buildPdfRenderableQuestions = ({
  sectionQuestions,
  formValues,
  getQuestionNumber,
  formatValue,
  getOrdinalWord,
  maxRepeatCount,
}: BuildPdfRenderableQuestionsArgs): PdfRenderableQuestion[] => {
  const renderedQuestions = new Set<number>();
  const renderableQuestions: PdfRenderableQuestion[] = [];

  sectionQuestions.forEach((question) => {
    if (renderedQuestions.has(question.id)) return;

    const repeatingQuestions = sectionQuestions.filter(
      (q) => q.conditional_logic?.repeat_based_on === question.id
    );
    const questionNumber = getQuestionNumber(question.id);

    if (repeatingQuestions.length > 0) {
      renderedQuestions.add(question.id);
      repeatingQuestions.forEach((q) => renderedQuestions.add(q.id));

      const rawRepeatCount = Number(formValues[`question_${question.id}`]) || 0;
      const repeatCount = Math.min(Math.max(0, rawRepeatCount), maxRepeatCount);
      const controllerValue = formValues[`question_${question.id}`];

      const repeatedItems: PdfRepeatedItem[] = Array.from({ length: repeatCount }, (_, index) => {
        const answers = repeatingQuestions.map((repeatingQuestion) => {
          const repeatedFieldName = `question_${repeatingQuestion.id}_${index}`;
          const repeatedValue = formValues[repeatedFieldName];
          const repeatedFormattedValue =
            formatValue(repeatedValue, repeatingQuestion.type) || 'No response provided';
          const repeatingLabel = repeatingQuestion.conditional_logic?.substitute_index
            ? repeatingQuestion.text.replace('[first]', getOrdinalWord(index + 1))
            : `${repeatingQuestion.text} (${index + 1})`;

          return {
            key: `${repeatingQuestion.id}_${index}`,
            label: repeatingLabel,
            value: repeatedFormattedValue,
          };
        });

        return {
          key: `${question.id}_group_${index}`,
          itemLabel: `Item ${index + 1}`,
          answers,
        };
      });

      renderableQuestions.push({
        key: String(question.id),
        questionNumber,
        label: question.text,
        value: formatValue(controllerValue, question.type) || 'No response provided',
        repeatedItems,
      });
      return;
    }

    if (question.conditional_logic?.repeat_based_on) {
      renderedQuestions.add(question.id);
      return;
    }

    renderedQuestions.add(question.id);
    const value = formValues[`question_${question.id}`];
    renderableQuestions.push({
      key: String(question.id),
      questionNumber,
      label: question.text,
      value: formatValue(value, question.type) || 'No response provided',
    });
  });

  return renderableQuestions;
};
